import { Box, Grid, Stack, useMediaQuery, useTheme } from '@mui/material'
import { type ReactElement, useMemo, useRef, useState } from 'react'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { mediaSelectors } from '@/features/media/slice'
import { segmentArray } from '@/layout/helpers'
import { useAppSelector } from '@/redux/hooks'
import { usePerformMediaBulkEditMutation } from '../api'
import { selectActiveValuesForSubmission } from '../selectors'
import type { Field, FieldName } from '../types'
import { buildBulkEditContent } from '../utils'
import { FormActions } from './FormActions'
import InputContainer from './InputContainer'
import AllEditActionButtons from './inputs/SetAllActionsButtons'
import ReviewDialog from './review/ReviewDialog'

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'bulk edit form',
  formId: 'bulk-edit-form',
  formLocation: 'main',
  formDestination: 'emc/performMediaBulkEdit',
  formSubmitText: 'review and submit',
}

export default function BulkEditForm() {
  const gaFormRef = useRef<GaFormHandle>(null)
  const { fields } = useAppSelector((state) => state.reducer.bulkEdit)
  const isPerkins = useAppSelector(mediaSelectors.selectIsPerkins)
  const [dialogOpen, setDialogOpen] = useState(false)
  const platform = isPerkins ? 'perkins' : 'cat'
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const columnsCount = isSmall ? 1 : 3
  const [submit, submitStatus] = usePerformMediaBulkEditMutation()
  const formContent = useAppSelector(selectActiveValuesForSubmission())
  const bulkEditContent = useMemo(
    () => buildBulkEditContent(fields as Record<string, Field>, formContent),
    [fields, formContent],
  )

  function isFieldOfSamePlatform(field: Field) {
    return field.platform === undefined || field.platform === platform
  }

  const fieldsToRender = useMemo(() => {
    const fieldsToRender: ReactElement[] = []

    let fieldName: FieldName
    for (fieldName in fields) {
      if (isFieldOfSamePlatform(fields[fieldName])) {
        fieldsToRender.push(<InputContainer key={fieldName} name={fieldName} />)
      }
    }

    const columns = segmentArray(fieldsToRender, columnsCount).map((c, i) => (
      <Grid key={i} size={{ xs: 12, md: 4 }}>
        {c}
      </Grid>
    ))

    return columns
  }, [fields, platform, columnsCount])

  const gaFormProps: Omit<GaFormProps, 'children'> = {
    ...baseGaFormProps,
    formContent: bulkEditContent,
    formStatus: submitStatus.isError ? 'error' : 'success',
    deferGaSubmit: true,
  }

  return (
    <GaForm ref={gaFormRef} {...gaFormProps}>
      <Box py={3}>
        <Stack gap={2}>
          <AllEditActionButtons />
          <Grid container spacing={3}>
            {fieldsToRender}
          </Grid>
          <FormActions onSubmit={() => setDialogOpen(true)} />
        </Stack>
      </Box>
      <ReviewDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        gaFormRef={gaFormRef}
        submit={submit}
        submitStatus={submitStatus}
      />
    </GaForm>
  )
}
